import React, { useEffect, Fragment } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import AlertBar from '../../components/alert-bar';
import dayjs from 'dayjs';
import {
  ButtonWithAction,
  Box,
  BoxWithShadow,
  Cluster,
  constants,
  DefaultPageTemplate,
  Loading,
  SolidDivider,
  Detail,
  Title,
} from '@thecb/components';
import { CASH_ACTION_TYPES } from '../../utils/constants';
import { formatPrice } from '../../utils/helpers';
import { StatusModule } from '../../components/status-module';
import { useCloseoutReportDownloader } from '../../components/pdf-reports';

const Header = ({ alertBar, alertBarActions }) =>
  alertBar.alerts.length > 0 && (
    <Box padding="16px 0">
      <AlertBar {...alertBar} {...alertBarActions} isToast={true} />
    </Box>
  );

const CashActionDetails = ({
  data: cashAction,
  fetching,
  alertBars,
  actions,
  getCashActionDetails,
  getCloseoutDetails,
  error,
  errorText,
  submitPrintReceipt,
  submitReprintReport,
  typeDisplay,
  reports,
}) => {
  // this type value will be used to influence UI rendering
  // should either be cashDrop or closeout (cashAdd in the future)
  const { type, cashier, total } = cashAction;
  const { id } = useParams();
  const { pathname } = useLocation();
  const closeoutDetailsVariant = type === CASH_ACTION_TYPES.CLOSEOUT;
  const cashActionDetailsVariant = type !== CASH_ACTION_TYPES.CLOSEOUT;
  useEffect(() => {
    if (pathname.includes(CASH_ACTION_TYPES.CLOSEOUT)) {
      getCloseoutDetails(id);
    } else {
      getCashActionDetails(id);
    }
  }, [pathname]);
  useCloseoutReportDownloader(reports);

  const buttonStyle = `
    border-radius: 4px;
    font-size: 14px;
  `;
  const handleReprintClick = (e) => {
    e.preventDefault();
    submitReprintReport(id);
  };

  const handleSubmitPrintReceipt = (e) => {
    e.preventDefault();
    submitPrintReceipt(id);
  };

  const cashActionActions = () => (
    <Cluster align="center">
      {cashActionDetailsVariant && (
        <ButtonWithAction
          extraStyles={buttonStyle}
          text="Print Receipt"
          isLoading={false}
          variant="secondary"
          action={handleSubmitPrintReceipt}
          textExtraStyles="font-size: 1rem;"
        />
      )}
      {closeoutDetailsVariant && (
        <ButtonWithAction
          extraStyles={buttonStyle}
          text="Reprint reports"
          isLoading={false}
          variant="secondary"
          action={handleReprintClick}
          textExtraStyles="font-size: 1rem;"
        />
      )}
    </Cluster>
  );

  const dateCluster = cashActionDetailsVariant ? (
    <Cluster justify="space-between" extraStyles={`margin-bottom: 0.5rem;`}>
      <Detail
        variant="small"
        weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
      >
        Date
      </Detail>
      <Detail variant="small">
        {dayjs(cashAction.date).format('MM/DD/YYYY hh:mmA')}
      </Detail>
    </Cluster>
  ) : (
    <div>
      <Cluster justify="space-between" extraStyles={`margin-bottom: 0.5rem;`}>
        <Detail
          variant="small"
          weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
        >
          Opened Date
        </Detail>
        <Detail variant="small">
          {dayjs(cashAction?.terminal_session?.created_at).format(
            'MM/DD/YYYY hh:mmA',
          )}
        </Detail>
      </Cluster>
      <Cluster justify="space-between" extraStyles={`margin-bottom: 0.5rem;`}>
        <Detail
          variant="small"
          weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
        >
          Closed Date
        </Detail>
        <Detail variant="small">
          {dayjs(cashAction?.terminal_session?.closeout_at).format(
            'MM/DD/YYYY hh:mmA',
          )}
        </Detail>
      </Cluster>
    </div>
  );

  const body =
    error || Boolean(!cashAction) ? (
      <Box>
        <Detail>Error</Detail>
        <Detail>Sorry, {errorText}</Detail>
      </Box>
    ) : (
      <Fragment>
        <Box padding="1rem 0">
          <BoxWithShadow
            variant="baseStandard"
            padding="0"
            borderRadius=".25rem"
          >
            <Box
              background={constants.colors.ATHENS_GREY}
              borderRadius=".25rem"
            >
              <Cluster justify="space-between" align="center">
                <Title
                  id="cash-action-title"
                  variant="small"
                  as="h4"
                  color={constants.colors.CHARADE_GREY}
                  weight="600"
                >
                  {'Cash Action ID: '}
                  <Detail
                    as="span"
                    variant="large"
                    weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                    extraStyles={`margin-left: 0.25rem; font-size: 1.25rem;`}
                  >
                    {/* Must do this for now because the cashAction vs closeout JSON is formatted and parsed differently. */}
                    {closeoutDetailsVariant && cashAction?.closeout?.id}
                    {cashActionDetailsVariant && cashAction?.id}
                  </Detail>
                </Title>
                <Detail
                  fontSize="20px"
                  weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                  id="cash-action-total-1"
                >
                  {formatPrice(total)}
                </Detail>
              </Cluster>
            </Box>
            <SolidDivider />
            <Box background={constants.colors.WHITE} borderRadius=".25rem">
              <Cluster justify="space-between" align="center">
                <Box padding="0">
                  <StatusModule label={typeDisplay} status="completed" />
                </Box>
                {cashActionActions()}
              </Cluster>
            </Box>
          </BoxWithShadow>
        </Box>

        {/* CASH ACTION INFO SECTION */}
        <Box padding="1rem 0">
          <Title
            as="h4"
            variant="small"
            color={constants.colors.CHARADE_GREY}
            weight="600"
          >
            {typeDisplay} Details
          </Title>
          <BoxWithShadow
            variant="baseStandard"
            padding="0"
            background={constants.colors.WHITE}
            borderRadius=".25rem"
          >
            <Box padding="1.5rem" width="384px" borderRadius=".25rem">
              {dateCluster}
              <Cluster
                justify="space-between"
                extraStyles={`margin-bottom: 0.5rem;`}
              >
                <Detail
                  variant="small"
                  weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                >
                  User
                </Detail>
                <Detail variant="small">{cashier}</Detail>
              </Cluster>
              <Cluster
                justify="space-between"
                extraStyles={`margin-bottom: 0.5rem;`}
              >
                <Detail
                  variant="small"
                  weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                >
                  Terminal
                </Detail>
                <Detail variant="small">{cashAction.terminal}</Detail>
              </Cluster>

              <Cluster
                justify="space-between"
                extraStyles={`margin-bottom: 0.5rem;`}
              >
                <Detail
                  variant="small"
                  weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}
                >
                  Batch Number
                </Detail>
                <Detail variant="small">{cashAction.batchNumber}</Detail>
              </Cluster>
            </Box>
            {cashActionDetailsVariant && (
              <Fragment>
                <SolidDivider />
                <Box
                  background={constants.colors.ATHENS_GREY}
                  padding="0 1.5rem"
                  borderRadius=".25rem"
                >
                  <Box padding="1.5rem 0">
                    <Cluster justify="space-between">
                      <Detail weight={constants.fontWeights.FONT_WEIGHT_BOLD}>
                        {typeDisplay} Total
                      </Detail>
                      <Detail
                        variant="large"
                        weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                        id="cash-action-total-2"
                      >
                        {formatPrice(total)}
                      </Detail>
                    </Cluster>
                  </Box>
                </Box>
              </Fragment>
            )}
          </BoxWithShadow>
        </Box>
      </Fragment>
    );

  return (
    <DefaultPageTemplate
      header={
        <Header
          key="CashActionDetailsHeader"
          alertBar={alertBars.cashActionDetailsAlertBars}
          alertBarActions={actions.alertBars.cashActionDetailsAlertBars}
        />
      }
      gutters="0"
      content={fetching ? <Loading /> : body}
      maxWidth="85.714rem"
    />
  );
};

export default CashActionDetails;
